/**
 * useSlotPicker: the service page's date and time picker state, shared by the Mini App and the
 * studio website (shared-client-screens D3, rows S3, S4). Each side reads its own open days and
 * day times and hands them in; the picker builds the date strip, picks the first open day once
 * every read has settled, restores a starting time, and keeps the picked time.
 *
 * The starting time is the app's held time when the client backs into the page from a later step
 * (its day is picked at once, even over a first-day pick made before the flow read landed), or the
 * website's time in the link (its day is picked once the days have settled, when it is open).
 * Either way its time is picked when its day's times arrive, and a pick by the client ends it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { HubServiceSlot } from '@/api/types';
import type { DateTimePickerProps } from './components/DateTimePicker';
import { bucketSlots, buildDateStrip, dayTzLabel, maxYmd, minYmd, ymdFromIso } from './detailFormat';

export interface SlotPickerDates {
  /** The open days read so far, YYYY-MM-DD in the client's zone. A failed or loading read adds none. */
  available: Set<string>;
  /** No read is still in its first load. */
  settled: boolean;
  /** Every read failed. */
  failed: boolean;
}

export interface SlotPickerDay {
  slots: HubServiceSlot[];
  /** The picked day's first load, with nothing to show yet. */
  loading: boolean;
  /** A newly picked day's times load while the last day's still show, dimmed. */
  updating: boolean;
}

export interface SlotPickerStart {
  startsAt: string;
  /** The client already holds it (the app's re-entry). */
  held: boolean;
}

export interface UseSlotPickerInput<D extends SlotPickerDay> {
  tz: string;
  /** False while the offering takes no bookings or its read has not landed: the strip waits. */
  enabled: boolean;
  dates: SlotPickerDates;
  /** Reads the picked day's times; a hook, called on every render. */
  useDay: (date: string | null) => D;
  start?: SlotPickerStart | null;
  /** The client picked a day (null) or a time; never called for the start's restore. */
  onPick?: (slot: HubServiceSlot | null) => void;
}

export interface SlotPicker<D extends SlotPickerDay> {
  selectedDate: string | null;
  selectedSlot: HubServiceSlot | null;
  /** Drops the picked time (it was just taken). */
  clearSlot: () => void;
  /** The picked day's read, as `useDay` returned it. */
  day: D;
  /** Everything DateTimePicker draws. */
  picker: DateTimePickerProps;
}

type StartPhase = 'day' | 'slot' | 'done';

const sameInstant = (a: string, b: string) => new Date(a).getTime() === new Date(b).getTime();

export function useSlotPicker<D extends SlotPickerDay>({
  tz,
  enabled,
  dates,
  useDay,
  start = null,
  onPick,
}: UseSlotPickerInput<D>): SlotPicker<D> {
  const { available, settled, failed } = dates;
  const firstAvailable = useMemo(() => minYmd(available), [available]);
  // The strip runs from the first open day to the last: no greyed lead-in, no greyed tail.
  const strip = useMemo(() => buildDateStrip(tz, available, maxYmd(available), firstAvailable), [tz, available, firstAvailable]);
  // "Still loading" and "every read failed" are told apart from a settled empty result, so the picker
  // never flashes an all-disabled strip that reads as no availability.
  const datesLoading = !enabled || (!settled && available.size === 0);
  const datesError = settled && failed && available.size === 0;

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<HubServiceSlot | null>(null);
  const [startPhase, setStartPhase] = useState<StartPhase>('day');
  const startDay = start ? ymdFromIso(start.startsAt, tz) : null;

  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  // The start's day.
  useEffect(() => {
    if (!start || !startDay || startPhase !== 'day') return;
    if (start.held) {
      setSelectedDate(startDay);
      setStartPhase('slot');
      return;
    }
    if (!settled) return;
    if (available.has(startDay)) {
      setSelectedDate(startDay);
      setStartPhase('slot');
    } else setStartPhase('done');
  }, [start, startDay, startPhase, settled, available]);

  // The first open day, once every read has settled (an out-of-order month can't win), unless the
  // start's day is still to come.
  useEffect(() => {
    if (selectedDate || !settled) return;
    if (start && startPhase === 'day') return;
    const first = strip.find((c) => c.available);
    if (first) setSelectedDate(first.ymd);
  }, [selectedDate, settled, start, startPhase, strip]);

  const day = useDay(selectedDate);

  // The start's time, once its day's own times are in.
  useEffect(() => {
    if (!start || startPhase !== 'slot' || selectedDate !== startDay) return;
    const match = day.slots.find((s) => sameInstant(s.startsAt, start.startsAt));
    if (match) {
      setSelectedSlot(match);
      setStartPhase('done');
    } else if (!day.loading && !day.updating) setStartPhase('done');
  }, [start, startPhase, selectedDate, startDay, day.slots, day.loading, day.updating]);

  const selectDate = useCallback((ymd: string) => {
    setSelectedDate(ymd);
    setSelectedSlot(null);
    setStartPhase('done');
    onPickRef.current?.(null);
  }, []);

  const selectSlot = useCallback((slot: HubServiceSlot) => {
    setSelectedSlot(slot);
    setStartPhase('done');
    onPickRef.current?.(slot);
  }, []);

  const clearSlot = useCallback(() => setSelectedSlot(null), []);
  const periodGroups = useMemo(() => bucketSlots(day.slots, tz), [day.slots, tz]);

  return {
    selectedDate,
    selectedSlot,
    clearSlot,
    day,
    picker: {
      dates: strip,
      selectedDate,
      onSelectDate: selectDate,
      tzLabel: dayTzLabel(tz, selectedDate, day.slots),
      tz,
      datesLoading,
      datesError,
      slotsLoading: day.loading,
      slotsUpdating: day.updating,
      periodGroups,
      selectedSlot: selectedSlot?.startsAt ?? null,
      onSelectSlot: selectSlot,
    },
  };
}
